import * as React from 'react';
import { useEffect } from 'react';

import { translate } from '~l10n/client';
import cx from 'classnames';
import { useAtom } from 'jotai';

import { Container } from '~shared/components/Container/Container';
import { RadioGroup, RadioItem } from '~shared/components/RadioGroup';
import { deliveryMethodAtom } from '~teacher/launchv2/atoms';
import { useIsRoomRostered } from '~teacher/launchv2/context/RoomContext';
import { InstantIcon } from '~teacher/launchv2/icons/InstantIcon';
import { OpenIcon } from '~teacher/launchv2/icons/OpenIcon';
import { PacedIcon } from '~teacher/launchv2/icons/PacedIcon';

import { DeliveryMethod } from '../../DeliveryMethod';

export function DeliveryMethodSelection() {
  const isRoomRostered = useIsRoomRostered();
  const [deliveryMethod, setDeliveryMethod] = useAtom(deliveryMethodAtom);

  useEffect(() => {
    setDeliveryMethod(DeliveryMethod.InstantFeedback, isRoomRostered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDeliveryMethodChanged = (e: React.FormEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value as DeliveryMethod;
    setDeliveryMethod(value, isRoomRostered);
  };
  return (
    <div>
      <RadioGroup
        layout="stack"
        id="deliveryMethod"
        value={deliveryMethod}
        onChange={onDeliveryMethodChanged}
      >
        <RadioItem
          value={DeliveryMethod.InstantFeedback}
          label={translate('Instant Feedback')}
        >
          {({ checked }) => (
            <DeliveryRadioItem
              name={translate('Instant Feedback')}
              checked={checked}
              message={translate(
                'Students answer questions in order and cannot change answers. Instant feedback is provided after each question. You monitor progress in a table of live results.'
              )}
              icon={<InstantIcon />}
            />
          )}
        </RadioItem>
        <RadioItem
          value={DeliveryMethod.OpenNavigation}
          label={translate('Open Navigation')}
        >
          {({ checked }) => (
            <DeliveryRadioItem
              name={translate('Open Navigation')}
              checked={checked}
              message={translate(
                'Students may answer questions in any order and change answers before finishing. You monitor progress in a table of live results.'
              )}
              icon={<OpenIcon />}
            />
          )}
        </RadioItem>
        <RadioItem
          value={DeliveryMethod.TeacherPaced}
          label={translate('Teacher Paced')}
        >
          {({ checked }) => (
            <DeliveryRadioItem
              name={translate('Teacher Paced')}
              checked={checked}
              message={translate(
                'You control the flow of questions and monitor responses as they happen. You may skip and revisit questions.'
              )}
              icon={<PacedIcon />}
            />
          )}
        </RadioItem>
      </RadioGroup>
    </div>
  );
}

function DeliveryRadioItem({
  name,
  checked,
  message,
  icon,
}: {
  name: string;
  checked: boolean;
  message: string;
  icon: JSX.Element;
}) {
  const classname = cx(
    'p-6 border border-solid w-full cursor-pointer rounded-2 text-center border-grey-100 box-border relative',
    {
      ['border-cyan-500 border-solid']: checked,
    },
    {
      ['hover:bg-grey-100 border']: !checked,
    }
  );

  return (
    <div className={classname}>
      <Container row verticalCenter>
        <div style={{ height: '32px' }} className={'flex-shrink-0'}>
          {icon}
        </div>
        <span
          className={
            'ml-4 text-left text-blue-900 text-display font-display font-500 flex-grow'
          }
        >
          {name}
        </span>
        <Indicator checked={checked} />
      </Container>
      {checked ? (
        <Container className={'mt-4'}>
          <span className={'text-base text-blue-900 leading-8 text-left'}>
            {message}
          </span>
        </Container>
      ) : null}
    </div>
  );
}

const Indicator = ({ checked }: { checked: boolean }) => {
  return (
    <div
      className={cx(
        'h-6 w-6 rounded-round relative border-solid border-cyan-500 overflow-hidden flex-shrink-0'
      )}
      style={{ borderWidth: checked ? '6px' : '2px' }}
    />
  );
};
